import React, { ReactNode, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import cn from 'classnames';
import Heading from '@components/ui/Heading';
import IconWrapper from '@components/ui/Icons';

import styles from './Modal.module.scss';

const MODAL_PORTAL_ID = 'modal-portal';

export interface ModalProps {
  isOpen: boolean;
  title?: string;
  label?: string;
  labelledBy?: string;
  fixedInHeader?: ReactNode;
  classNames?: string | string[];
  containerClassNames?: string | string[];
  scrollableClassNames?: string | string[];
  onClose?: () => void;
  children: ReactNode;
  fitContent?: boolean;
  isSquare?: boolean;
  isLandscape?: boolean;
  noPadding?: boolean;
  circleCloseButton?: boolean;
  bottomPositioning?: boolean;
  closeButtonVisible?: boolean;
}

export interface ModalLabelledByProps extends ModalProps {
  labelledBy: string;
}

export interface ModalLabelProps extends ModalProps {
  label: string;
}

export interface ModalTitleProps extends ModalProps {
  title: string;
}

export interface ModalFixedHeaderProps extends ModalProps {
  labelledBy: string;
  fixedInHeader: ReactNode;
}

const Modal = ({
  isOpen,
  title,
  label,
  labelledBy,
  fixedInHeader,
  onClose,
  classNames,
  containerClassNames,
  scrollableClassNames,
  children,
  fitContent,
  isSquare = false,
  isLandscape = false,
  circleCloseButton = false,
  noPadding = false,
  bottomPositioning = false,
  closeButtonVisible = true,
}:
  | ModalFixedHeaderProps
  | ModalLabelledByProps
  | ModalLabelProps
  | ModalTitleProps) => {
  const t = useTranslationWithSelfFallback('common');
  const headingId = useId();
  const labelId = title && title.length > 0 ? headingId : labelledBy;

  const ariaProps = labelId
    ? {
        'aria-labelledby': labelId,
      }
    : { 'aria-label': label };

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setPortalTarget(document.getElementById(MODAL_PORTAL_ID));
  }, []);

  const modalContent = (
    <>
      <div
        role="dialog"
        aria-modal
        {...ariaProps}
        tabIndex={-1}
        className={cn(styles.overlay, classNames)}
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
        onKeyDown={(e) => e.key === 'Escape' && onClose?.()}
      >
        <div
          className={cn(styles.container, containerClassNames, {
            [styles.fitContent]: fitContent,
            [styles.square]: isSquare,
            [styles.landscape]: isLandscape,
            [styles.noPadding]: noPadding,
            [styles.bottomPositioning]: bottomPositioning,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          {closeButtonVisible && onClose && (
            <button
              type="button"
              className={cn(styles.closeBtn, {
                [styles.circleCloseButton]: circleCloseButton,
              })}
              onClick={onClose}
            >
              <span className={styles.screenReader}>{t('close')}</span>
              <span aria-hidden>
                <IconWrapper classNames={styles.icon} icon="close" width={24} />
              </span>
            </button>
          )}
          {title && (
            <Heading
              semanticLevel="h2"
              styleLevel="3"
              className={styles.heading}
            >
              <span id={headingId}>{title}</span>
            </Heading>
          )}
          {fixedInHeader}
          <div
            className={cn(styles.scrollable, scrollableClassNames, {
              [styles.noPadding]: noPadding,
            })}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );

  if (isOpen && portalTarget) {
    return createPortal(modalContent, portalTarget);
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onDismiss={onClose}
      className={cn(styles.overlay, classNames)}
    >
      <DialogContent
        className={cn(styles.container, containerClassNames, {
          [styles.fitContent]: fitContent,
          [styles.square]: isSquare,
          [styles.landscape]: isLandscape,
          [styles.noPadding]: noPadding,
          [styles.bottomPositioning]: bottomPositioning,
        })}
        {...ariaProps}
      >
        {closeButtonVisible && onClose && (
          <button
            type="button"
            className={cn(styles.closeBtn, {
              [styles.circleCloseButton]: circleCloseButton,
            })}
            onClick={onClose}
          >
            <span className={styles.screenReader}>{t('close')}</span>
            <span aria-hidden>
              <IconWrapper classNames={styles.icon} icon="close" width={24} />
            </span>
          </button>
        )}
        {title && (
          <Heading semanticLevel="h2" styleLevel="3" className={styles.heading}>
            <span id={headingId}>{title}</span>
          </Heading>
        )}
        {fixedInHeader}
        <div
          className={cn(styles.scrollable, scrollableClassNames, {
            [styles.noPadding]: noPadding,
          })}
        >
          {children}
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};

export default Modal;
